import styled from 'styled-components';
import { Dialog, Popover } from '@mui/material';
import { COLOR } from '@src/styles/color';

const SCROLLBAR = `
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLOR.indigo[48]};
    border-radius: 4px;
  }
`;

// Five rows fit before the list starts scrolling, matching the longest list the
// design shows open.
const MENU_MAX_HEIGHT = 272;

export const StyledSelectPopover = styled(Popover)`
  /* The list renders in a portal, out of reach of the dialog's own reset. */
  .MuiPopover-paper,
  .MuiPopover-paper * {
    box-sizing: border-box;
  }

  .MuiPopover-paper {
    width: ${({ anchorWidth }) => (anchorWidth ? `${anchorWidth}px` : 'auto')};
    max-height: ${MENU_MAX_HEIGHT}px;
    margin-top: 4px;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    overflow-y: auto;
    ${SCROLLBAR}
  }

  [role='listbox'] {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .survey-select-option {
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    font-family: inherit;
    cursor: pointer;

    &:hover,
    &[aria-selected='true'] {
      background: ${COLOR.indigo[8]};
    }
  }
`;

export const StyledSurveyPopup = styled(Dialog)`
  /* The app sets no global box-sizing, so every padded box here states it —
     otherwise a width of 100% plus padding overflows and gets clipped. */
  .MuiDialog-paper,
  .MuiDialog-paper * {
    box-sizing: border-box;
  }

  .MuiDialog-paper {
    width: 640px;
    height: 720px;
    max-width: 640px;
    margin: 0;
    /* No padding on the sides: the scrolling form reaches the paper edge so its
       scrollbar sits against it, and each section pads itself instead. */
    padding: 24px 0;
    gap: 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overflow: hidden;
    border-radius: 12px;
    background: ${COLOR.white};
  }

  .survey-header {
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }

  .survey-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .survey-icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    object-fit: cover;
  }

  .survey-title {
    flex: 1 0 0;
    word-break: break-word;
  }

  .survey-subtitle {
    width: 100%;
  }

  .survey-forms {
    flex: 1 0 0;
    min-height: 0;
    width: 100%;
    /* 12px on the right leaves the questions level with the header once the
       12px scrollbar gutter is added. */
    padding: 0 12px 0 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    overflow-x: clip;
    overflow-y: auto;
    /* Reserve the gutter so the questions do not shift when the survey is short
       enough not to scroll. */
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    /* A transparent border clipped away leaves an 8px thumb centred in the 12px
       track, so it stands 2px clear of the paper edge. */
    &::-webkit-scrollbar-thumb {
      background: ${COLOR.indigo[48]};
      background-clip: content-box;
      border: 2px solid transparent;
      border-radius: 6px;
    }
  }

  .survey-question {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .survey-question-label {
    width: 100%;
  }

  .survey-select {
    width: 100%;
  }

  /* Native buttons do not inherit the page font — only body carries it — so
     the trigger restates it or its text falls back to the browser default. */
  .survey-select-trigger {
    font-family: inherit;
    width: 100%;
    height: 40px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: ${COLOR.white};
    border: 1px solid ${COLOR.indigo[16]};
    border-radius: 8px;
    cursor: pointer;
  }

  .survey-select-arrow {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: ${COLOR.indigo[80]};
  }

  .survey-tags {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    gap: 12px;
  }

  /* The selected tag keeps a border in its own colour so it never sizes
     differently from an unselected one. */
  .survey-tag {
    height: 40px;
    padding: 8px 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: ${COLOR.white};
    border: 1px solid ${COLOR.indigo[16]};
    border-radius: 8px;
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;

    &[aria-pressed='true'] {
      background: ${COLOR.yellow.default};
      border-color: ${COLOR.yellow.default};
    }
  }

  .survey-other {
    width: 100%;

    .input-label {
      display: none;
    }

    .MuiOutlinedInput-root {
      min-height: 96px;
      padding: 12px;
      align-items: flex-start;
      border-radius: 12px;
    }

    .MuiOutlinedInput-notchedOutline {
      border: 0.5px solid ${COLOR.indigo[16]};
    }

    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: 0.5px solid ${COLOR.indigo[16]};
    }

    textarea {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.4px;
      color: ${COLOR.darkGray};

      &::placeholder {
        color: ${COLOR.label2[48]};
        opacity: 1;
      }
    }

    .MuiInputAdornment-root {
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.4px;
      color: ${COLOR.label2[48]};
    }
  }

  .survey-footer {
    width: 100%;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;
